import React, { FC } from 'react';
import { ScrollView, Text, View } from 'react-native';
import i18n from 'i18n-js';
import Svg, { Path } from 'react-native-svg';
import ColorWrapper, { WithColor } from '@styles/colors';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import TouchableElement from '@components/common/touchableElement';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { MOBILE_ANNOTATION_LIST_EXPANDED_HEIGHT, MOBILE_ANNOTATION_LIST_HEADER_HEIGHT } from '@v2/components/imageHandling/components/DrawingCanvas/constants';
import { Annotation } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import { useManageMobileAnnotationList } from './hooks/useManageMobileAnnotationList';

interface MobileAnnotationListProps extends WithColor {
  annotations: Annotation[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const MobileAnnotationList: FC<MobileAnnotationListProps> = ({ C, annotations, selectedId, onSelect, isExpanded, onToggleExpanded }) => {
  const { annotationsCount, handleHeaderPress, handleItemPress, getItemLabel } = useManageMobileAnnotationList({
    annotations,
    selectedId,
    onSelect,
    onToggleExpanded,
  });

  return (
    <View
      testID={TEST_IDS.drawingCanvas.annotationList}
      style={{
        backgroundColor: C('Background/Light/Panel [web]'),
        borderTopWidth: 1,
        borderTopColor: C('Content/Light/Tertiary'),
      }}
    >
      <TouchableElement onPress={handleHeaderPress}>
        {() => (
          <View
            style={{
              height: scale(MOBILE_ANNOTATION_LIST_HEADER_HEIGHT),
              flexDirection: 'row',
              alignItems: 'center',
              paddingHorizontal: scale(12),
              borderBottomWidth: isExpanded ? 1 : 0,
              borderBottomColor: C('Content/Light/Tertiary'),
            }}
          >
            <Text style={[font['Button 2'], { color: C('Label/Light/Primary'), flex: 1 }]}>
              {i18n.t('Annotations')} ({annotationsCount})
            </Text>
            <Svg width={scale(16)} height={scale(16)} viewBox="0 0 24 24">
              <Path
                d={isExpanded ? 'M6 15 L12 9 L18 15' : 'M6 9 L12 15 L18 9'}
                fill="none"
                stroke={C('Label/Light/Primary')}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </Svg>
          </View>
        )}
      </TouchableElement>
      {isExpanded ? (
        <ScrollView style={{ height: scale(MOBILE_ANNOTATION_LIST_EXPANDED_HEIGHT) }}>
          {annotations.length === 0 ? (
            <Text style={[font['Caption 1'], { padding: scale(12), color: C('Label/Light/Tertiary') }]}>
              {i18n.t('Draw on the image to create annotations.')}
            </Text>
          ) : null}
          {annotations.map((a, index) => (
            <TouchableElement key={a.id} testID={TEST_IDS.drawingCanvas.annotationListItem} onPress={() => handleItemPress(a.id)}>
              {({ boxStyle }) => (
                <View
                  style={[
                    boxStyle,
                    {
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: scale(8),
                      paddingHorizontal: scale(12),
                      paddingVertical: scale(8),
                      backgroundColor: selectedId === a.id ? C('Button/Light/Secondary') : 'transparent',
                      borderBottomWidth: 1,
                      borderBottomColor: C('Content/Light/Tertiary'),
                    },
                  ]}
                >
                  <View style={{ width: scale(10), height: scale(10), borderRadius: scale(5), backgroundColor: a.style.strokeColor }} />
                  <Text style={[font['Caption 1'], { color: C('Label/Light/Primary'), flex: 1 }]} numberOfLines={1}>
                    {getItemLabel(a, index)}
                  </Text>
                </View>
              )}
            </TouchableElement>
          ))}
        </ScrollView>
      ) : null}
    </View>
  );
};

export default ColorWrapper(MobileAnnotationList);
